import { Theme } from "@mui/material/styles";
import { CellClassParams, CellEditorSelectorResult, ColDef, ICellEditorParams, ICellRendererParams, RowHeightParams, ValueGetterParams } from "ag-grid-community";
import { addMinutes } from "date-fns";
import Enumerable from "linq";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IEntityValueDeviceModel, IEntityValueTagModel, IEntityValueTankModel, IEntityValueWellModel } from "../../common/models/IEntityValueModels";
import { ALL_TAGS_CATEGORY_ID, ITagCategory, SUMMARY_LISTING_TAGS_CATEGORY_ID } from "../../common/models/ITagCategory";
import { ITagModel } from "../../common/models/ITagModel";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { ITankStyle } from "../../common/models/TankStyle";
import { getDeviceRoute, getTagRoute, getTankRoute } from "../../common/utilities/RouteHelpers";
import { LiveDataStateItem } from "../../contexts/live-data-context/LiveDataContextTypes";
import { CommandStatusEnum } from "../../services/live-data-service/Models";
import i18n from "../../services/localization-service/i18n";
import { IFeetAndInchesCellEditorProps } from "../ag-grid-extensions/cell-editors/FeetAndInchesCellEditor";
import { INumericCellEditorProps } from "../ag-grid-extensions/cell-editors/NumericCellEditor";
import { tagValueComparator } from "../ag-grid-extensions/comparators/TagValueComparator";
import { RowDef } from "../ag-grid-extensions/models/RowDef";
import { ITagNameCellRendererProps } from "../ag-grid-extensions/renderers/TagNameRenderer";
import { ITagValueCellRendererProps } from "../ag-grid-extensions/renderers/TagValueRenderer";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { DeviceSubTypeEnum } from "../../common/enums/DeviceSubTypeEnum";
import { ObjectTagValuesMode } from './ObjectTagValuesMode';
import { RouteContextType } from "../../hooks/use-route-context/UseRouteContext";
import { ITankBattery } from "../../common/models/ITankBattery";
import _, { omit } from "lodash";
import { TagValueChartGroup, TagValueChartListItem } from "./ObjectTagValuesChartList";
import { ITagAndTatID } from "../../services/tag-value-data-service/Models";
import { ITagHistoryItem } from "../../services/tag-data-service/Models";
import { ITagViewState } from "../../services/state-data-service/Models";
import { ObjectTagValuesColumnOptionsState, ObjectTagValuesViewEnum } from "./ObjectTagValuesState";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { IListTextTagMapping_V2 } from "../../common/models/ILookupList";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { CustomCellEditorProps } from "ag-grid-react";
import { TagTypeEnum } from "../../common/enums/TagTypeEnum";
import { DataTypeEnum } from "../../common/enums/DataTypeEnum";
import { IAccessPermission } from "../../common/models/AccessPermission";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { ObjectTagValuesTypeEnum } from "../../services/object-tag-values-data-service/ConfigurationModels";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { getTotalFromFeetAndInchesText } from "../../common/utilities/FeetAndInches";

const UNGROUPED_CATEGORY_ID: number = 999999;
const OIL_TANK_CATEGORY_ID: number = 1999999;
const WATER_TANK_CATEGORY_ID: number = 2999999;
const WELL_DEVICE_CATEGORY_ID: number = 3999999;



const ungroupedTagCategory: ITagCategory = {
    id: UNGROUPED_CATEGORY_ID,
    text: i18n.t('tag.ungrouped'),
    order: UNGROUPED_CATEGORY_ID
};


export const allTagsCategory: ITagCategory = {
    id: ALL_TAGS_CATEGORY_ID,
    text: i18n.t('category.allTags'),
    order: ALL_TAGS_CATEGORY_ID,
    selected: true
};

const summaryListingTagsCategory: ITagCategory = {
    id: SUMMARY_LISTING_TAGS_CATEGORY_ID,
    text: i18n.t('category.summaryListingTags'),
    order: SUMMARY_LISTING_TAGS_CATEGORY_ID
};

export const writePermission: IAccessPermission = {
    functionalityType: AccessFunctionalityTypeEnum.ObjectTagValuesConfiguration,
    access: AccessTypeEnum.Write_Change
};

const createDesktopGridColumns = (theme: Theme,
    offline: boolean,
    useLKVOffline: boolean,
    manualEntry: boolean,
    grouped: boolean,
    showTagDescriptions: boolean,
    configurationEnabled: boolean,
    configurationSelected: boolean,
    columnOptions: ObjectTagValuesColumnOptionsState,
    isElynxAdmin: boolean
): ColDef[] => {
    const cols: Array<ColDef & {omit?: boolean}> = new Array<ColDef & {omit?: boolean}>({
        field: "category",
        rowGroup: true,
        hide: true,
    }, {
        headerName: i18n.t("tag.label") as string,
        field: "name",
        sortable: true,
        sort: !configurationSelected ? "asc" : undefined,
        autoHeight: true,
        // flex: 2, // => Bug 51745: Tag Values Grid Autosize Columns
        cellRenderer: 'tagNameRenderer',
        cellRendererParams: {
            asAnchor: true,
            showTagDescription: showTagDescriptions
        } as ITagNameCellRendererProps,
        type: "text"
    }, {
        headerName: i18n.t("tag.value") as string,
        field: "value",
        sortable: true,
        comparator: tagValueComparator,
        cellRenderer: 'tagValueRenderer',
        filterValueGetter: (params: ValueGetterParams) => {
            return params.data.tag?.tagValue?.value;
        },
        cellRendererParams: {
            mobile: false,
            offline: offline,
            useLKVOffline: useLKVOffline,
            manualEntry: manualEntry
        } as ITagValueCellRendererProps,
        //editable: true,
        cellEditorSelector: (params: ICellRendererParams | ICellEditorParams): CellEditorSelectorResult => {

            const tag: IEntityValueTagModel = params.data?.tag;

            if (tag.features && (tag.features & ObjectFeaturesEnum.FeetAndInches) > 0) {

                if(!tag.tankStyle) {
                    return {
                      component: 'feetAndInchesMaskedCellEditor'
                    };

                } else {
                    return {
                        component: 'feetAndInchesCellEditor',
                        popup: true,
                        params: {
                            maxFeet: params.data.tankStyle?.heightInInches / 12,
                            label: tag.displayText
                        } as IFeetAndInchesCellEditorProps
                    };
                }
            } else if (tag.listID) {
                return {
                    component: 'agRichSelectCellEditor',
                    popup: true,
                    params: {
                        values: tag.lookupList?.listText.map((o) => o.description)
                    }
                };
            } else if (tag.features && (tag.features & ObjectFeaturesEnum.HoursMinutesSeconds)) {
                return {
                    component: 'timeCellEditor'
                };

            } else if (tag.dataType === DataTypeEnum.String) {
                return {
                    component: 'agTextCellEditor'
                };
            }else {
                return {
                    component: 'numericCellEditor'
                };
            }
        },
        //cellEditorParams: (params: ICellEditorParams) => {
        cellEditorParams: (params: CustomCellEditorProps) => {
            const tag: IEntityValueTagModel = params.data?.tag;
            const features: ObjectFeaturesEnum = tag.features as ObjectFeaturesEnum;

            if (!tag.listID && (features & ObjectFeaturesEnum.FeetAndInches) === 0) {
                const cellEditorParams: INumericCellEditorProps = {
                    ...params,
                    decimals: tag.decimalPlaces,
                    lowerBound: tag.writeLowerBound,
                    lowerBoundOp: tag.writeLowerBoundOp,
                    upperBound: tag.writeUpperBound,
                    upperBoundOp: tag.writeUpperBoundOp
                };
                return cellEditorParams;
            } else {
                return {...params,
                    lowerBound: tag.writeLowerBound,
                    lowerBoundOp: tag.writeLowerBoundOp,
                    upperBound: tag.writeUpperBound,
                    upperBoundOp: tag.writeUpperBoundOp
                };
            }
        },

        //cellStyle: (params: ICellRendererParams) => {
        cellStyle: (params: CellClassParams) => {

            return {
                //'color': params?.data?.tag?.color,
                fontWeight: params?.value?.tag?.color ? 'bold' : 'inherit',
                // SPR 2025-04-03 - Don't set padding for the cell, the grid will calculate it for us.
                // "lineHeight": `${theme.spacing(2)}`,
                // "padding": `${theme.spacing(1)}`,
                backgroundColor:
                    //2021-11-10 per Chris do not do cell shading for tanks
                    //params?.data?.tag?.tagObject.objectType === eLynxObjectTypeEnum.Tank &&
                    // params?.data?.tag?.tagValue?.setPointEventType === SetPointEventTypeEnum.Calculated ?
                    //     '#9995ed' :
                    //     params?.data?.tag?.tagObject?.objectType === eLynxObjectTypeEnum.Tank &&
                    //         params?.data?.tag?.tagValue?.setPointEventType === SetPointEventTypeEnum.Manual ?
                    //     '#c3bdf2' :
                    params?.data?.tag?.color ?
                        params?.data?.tag?.color :
                        params?.value?.color && params?.value?.value !== '' ? params?.value?.color :
                            'inherit'
            };

        },
        // flex: 1 // => Bug 51745: Tag Values Grid Autosize Columns
        type: "number"
    }, {
        headerName: i18n.t("uom.units") as string,
        field: "unit",
        sortable: true,
        // flex: 1 // => Bug 51745: Tag Values Grid Autosize Columns
        cellStyle: (params: CellClassParams) => {

            return {
                //'color': params?.data?.disabled ? 'red': 'inherit',
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };

        },
        type: "text"
    }, {
        headerName: i18n.t("datetime.timestamp") as string,
        field: 'timestamp',
        sortable: true,
        cellStyle: (params: CellClassParams) => {

            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };

        },
        type: "timestamp"
    }, {
        headerName: i18n.t("tag.uid") as string,
        field: 'uniqueIdentifiers',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !configurationEnabled || !columnOptions?.showUIDColumn
    }, {
        headerName: i18n.t("tag.readItem") as string,
        field: 'readItem',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !isElynxAdmin || !configurationEnabled || !columnOptions?.showReadItemColumn
    }, {
        headerName: i18n.t("tag.writeItem") as string,
        field: 'writeItem',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !isElynxAdmin || !configurationEnabled || !columnOptions?.showWriteItemColumn
    }, {
        headerName: "Live Data Read/Write", //i18n.t("tag.writeItem") as string,
        field: 'liveData',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: isElynxAdmin || !configurationEnabled || !columnOptions?.showLiveDataColumn
    }, {
        headerName: i18n.t("tag.lookupList") as string,
        field: 'listName',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !configurationEnabled || !columnOptions?.showLookupListColumn
    }, {
        headerName: i18n.t("tag.formula") as string,
        field: 'formula',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !configurationEnabled || !columnOptions?.showFormulaColumn
    })
    .filter(o => !o.omit)
    .map(o => omit(o, ["omit"]));

    // no row grouping, remove the column
    if (!grouped) {
        cols.shift();
    }

    return cols;
};

const createMobileGridColumns = (theme: Theme,
    offline: boolean,
    useLKVOffline: boolean,
    manualEntry: boolean,
    grouped: boolean,
    configurationEnabled: boolean,
    configurationSelected: boolean,
    columnOptions: ObjectTagValuesColumnOptionsState,
    isElynxAdmin: boolean
): ColDef[] => {

    const cols: Array<ColDef & {omit?: boolean}> = new Array<ColDef & {omit?: boolean}>({
        field: "category",
        rowGroup: true,
        hide: true,
    }, {
    headerName: i18n.t("tag.label") as string,
    field: "name",
    sortable: true,
    sort: !configurationSelected ? "asc" : undefined,
    // cellStyle: (params: CellClassParams) => {
    //     return {
    //         // SPR 2025-04-08 - When wrapText and autoHeight are set to true, the following
    //         // 3 lines should be included.
    //         lineHeight: "normal",
    //         paddingTop: "var(--ag-spacing)",
    //         paddingBottom: "var(--ag-spacing)",
    //     };
    // },
    // flex: offline ? 1 : 1.5, // => Bug 51745: Tag Values Grid Autosize Columns
    cellRenderer: 'tagNameRenderer',
    cellRendererParams: {
        hideTimestamp: (offline && !useLKVOffline) || manualEntry,
    } as ITagNameCellRendererProps,
    type: "text, multiline"
    }, {
        headerName: i18n.t('tag.value') as string,
        field: "value",
        sortable: true,
        // wrapText: true,
        // autoHeight: true,
        comparator: tagValueComparator,
        filterValueGetter: (params: ValueGetterParams) => {
            return params.data.tag?.tagValue?.value;
        },
        cellStyle: (params: CellClassParams) => {
            return {
                // SPR 2025-04-08 - Not sure why I still need to add this here because
                // the multiline column type should already do this.
                lineHeight: "normal",
                paddingTop: "var(--ag-spacing)",
                paddingBottom: "var(--ag-spacing)",

                //'color': params?.data?.tag?.color,
                // 'top': '0',
                fontWeight: params?.value?.tag?.color ? 'bold' : 'inherit',
                // SPR 2025-04-03 - Don't set padding for the cell, the grid will calculate it for us.
                // "lineHeight": `${theme.spacing(2)}`,
                // "padding": `${theme.spacing(1)}`,
                backgroundColor:
                    //2021-11-10 per Chris do not do cell shading for tanks
                    // params?.data?.tag?.tagObject.objectType === eLynxObjectTypeEnum.Tank &&
                    //             params?.data?.tag?.tagValue?.setPointEventType === SetPointEventTypeEnum.Calculated ?
                    //                 '#9995ed' :
                    //                 params?.data?.tag?.tagObject?.objectType === eLynxObjectTypeEnum.Tank &&
                    //                     params?.data?.tag?.tagValue?.setPointEventType === SetPointEventTypeEnum.Manual ?
                    //                 '#c3bdf2' :
                    params?.data?.tag?.color ?
                        params?.data?.tag?.color :
                        params?.value?.color && params?.value?.value !== '' ? params?.value?.color :
                            'inherit'

            };
        },
        // flex: 1, // => Bug 51745: Tag Values Grid Autosize Columns
        cellRenderer: 'tagValueRenderer',
        cellRendererParams: {
            mobile: true,
            offline: offline,
            useLKVOffline: useLKVOffline,
            manualEntry: manualEntry
        } as ITagValueCellRendererProps,
        //editable: manualEntry,
        singleClickEdit: true,
        cellEditorSelector: (params: ICellEditorParams | ICellRendererParams) => {

            const tag: IEntityValueTagModel = params.data?.tag;

            if (tag.features && (tag.features & ObjectFeaturesEnum.FeetAndInches) > 0) {

                
                return {
                    component: 'feetAndInchesCellEditor',
                    popup: true,
                    params: {
                        maxFeet: params.data.tankStyle?.heightInInches / 12,
                        label: tag.displayText
                    } as IFeetAndInchesCellEditorProps
                };
            } else if (tag.listID) {
                const listValues = tag.lookupList?.listText.map((o) => o.description);
                if (manualEntry) {
                    listValues?.unshift('');
                }
                return {
                    component: 'agRichSelectCellEditor',
                    popup: true,
                    params: {
                        values: listValues
                    }
                };
            } else {
                return {
                    component: 'numericCellEditor'
                };
            }
        },
        //cellEditorParams: (params: ICellEditorParams) => {
        cellEditorParams: (params: CustomCellEditorProps) => {
            const tag: IEntityValueTagModel = params.data?.tag;
            const features: ObjectFeaturesEnum = tag.features as ObjectFeaturesEnum;

            //console.log(`cell editor params: ${JSON.stringify(params.data)}`)

            if (!tag.listID && (features & ObjectFeaturesEnum.FeetAndInches) === 0) {
                const cellEditorParams: INumericCellEditorProps = {
                    ...params,
                    decimals: tag.decimalPlaces,
                    lowerBound: tag.writeLowerBound,
                    lowerBoundOp: tag.writeLowerBoundOp,
                    upperBound: tag.writeUpperBound,
                    upperBoundOp: tag.writeUpperBoundOp
                };
                return cellEditorParams;
            } else {
                return params;
            }
        },
        type: "number, multiline"
    }, {
        headerName: i18n.t("tag.uid") as string,
        field: 'uniqueIdentifiers',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !configurationEnabled || !columnOptions?.showUIDColumn
    }, {
        headerName: i18n.t("tag.readItem") as string,
        field: 'readItem',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !isElynxAdmin || !configurationEnabled || !columnOptions?.showReadItemColumn
    }, {
        headerName: i18n.t("tag.writeItem") as string,
        field: 'writeItem',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !isElynxAdmin || !configurationEnabled || !columnOptions?.showWriteItemColumn
    }, {
        headerName: "Live Data Read/Write", //i18n.t("tag.writeItem") as string,
        field: 'liveData',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: isElynxAdmin || !configurationEnabled || !columnOptions?.showLiveDataColumn
    }, {
        headerName: i18n.t("tag.lookupList") as string,
        field: 'listName',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !configurationEnabled || !columnOptions?.showLookupListColumn
    }, {
        headerName: i18n.t("tag.formula") as string,
        field: 'formula',
        sortable: true,
        cellStyle: (params: CellClassParams) => {
            return {
                //'color': params?.data?.tag?.color,
                'fontStyle': params?.data?.disabled ? 'italic' : 'inherit',
            };
        },
        type: "text",
        omit: !configurationEnabled || !columnOptions?.showFormulaColumn
    }).filter(o => !o.omit);

    // no row grouping, remove the column
    if (!grouped) {
        cols.shift();
    }

    return cols;
};

const projectRow = (category: string,
    categoryHref: string | undefined,
    order: number,
    tag: ITagModel,
    tankStyle: ITankStyle | undefined,
    routeContext: RouteContextType,
    offline: boolean,
    liveDataState: LiveDataStateItem | undefined,
    tankLevelInches: boolean
): RowDef => {

    const clonedTag = _.cloneDeep(tag);

    const row: RowDef = {
    };

    //const tagStatus = !liveDataState?.commandComplete ? liveDataState?.status?.deviceStatusList.flatMap(o => o.statusItems).find(o => o.tagID === tag.id) : undefined;
    const tagStatus = liveDataState?.status?.deviceStatusList.flatMap(o => o.statusItems).find(o => o.tagID === tag.id);

    //row['id'] = tag.id;
    row['id'] = `${clonedTag.categoryID ?? UNGROUPED_CATEGORY_ID}:${clonedTag.id}`;
    row['category'] = category;
    row['categoryHref'] = categoryHref;
    row['order'] = order;
    row['name'] = clonedTag.displayText?.trim();
    row['color'] = clonedTag.color;


    //console.log(`projectRow ${tag.id} value: ${tag.tagValue?.value ? tag.tagValue?.displayText : '...'}`);

    const isLiquidProductionTAT = (clonedTag.tatID === LiquidProductionTATEnum.TopLevel || 
                                    clonedTag.tatID === LiquidProductionTATEnum.InterfaceLevel ||
                                    clonedTag.tatID === LiquidProductionTATEnum.OilHeight ||
                                    clonedTag.tatID === LiquidProductionTATEnum.WaterHeight ||
                                    clonedTag.tatID === 100666 /* remaining capacity height */
                                    )
    
    row['value'] = clonedTag.tagValue?.value !== undefined ? 
                    tankLevelInches && isLiquidProductionTAT ?
                    getTotalFromFeetAndInchesText(clonedTag.tagValue?.displayText ?? '') :
                    clonedTag.tagValue?.displayText : '';
    row['timestamp'] = clonedTag.tagValue?.timestamp ?
        getTimeframeTimeDisplayText(clonedTag.tagValue?.displayTimeframe as TimeframeTypeEnum,
            new Date(clonedTag.tagValue?.timestamp as string)) :
        "";

    // row['value'] = tag.tagValue?.value;
    // row['timestamp'] = tag.tagValue?.timestamp ?
    //                         getTimeframeTimeDisplayText(tag.tagValue?.displayTimeframe as TimeframeTypeEnum,
    //                                                     new Date(tag.tagValue?.timestamp as string))  :
    //                         undefined;


    // row['unit'] = clonedTag.tagValue ? clonedTag.tagValue?.unitDisplayName || clonedTag.unitDisplayName : '';
    row['unit'] = clonedTag.tagValue?.unitDisplayName ?? clonedTag.unitDisplayName ?? '';

    row['tag'] = clonedTag;

    row['tankStyle'] = tankStyle;

    if (!offline) {
        row['href'] = getTagRoute(UIViewEnum.Tag_Details, routeContext.groupID, routeContext.wellID, routeContext.deviceID, routeContext.tankBatteryID, routeContext.tankID, clonedTag.id);
    }

    row['liveDataStatus'] = tagStatus ?
        tagStatus.status :
        liveDataState && !liveDataState.commandComplete && ((tag.features ?? 0) & ObjectFeaturesEnum.LiveData) > 0 ?
            CommandStatusEnum.InProgress :
            CommandStatusEnum.None;
    //row['liveDataStatus'] = CommandStatusEnum.None;

    row['hasWriteDown'] = ((tag.features ?? 0) & ObjectFeaturesEnum.Writedown) > 0 ? true : false;

    row['valid'] = true;

    row['disabled'] = tag.disabled;

    row['uniqueIdentifiers'] = tag.uniqueIdentifiers?.join(", ");
    row['readItem'] = tag.readItem;
    row['writeItem'] = tag.writeItem;
    row['liveData'] = (tag.writeItem?.length??0) > 0
        ? "Read/Write"
        : (tag.readItem?.length??0) > 0
            ? "Read Only"
            : "";
    row['listName'] = tag.listName;
    row['formula'] = tag.formula;
         
    return row;
};

const _hasLiveData = (o: IEntityValueDeviceModel |
    IEntityValueTankModel |
    IEntityValueWellModel |
    ITankBattery |
    IEntityValueTagModel |
    ITagModel) => (o.features !== undefined && (o.features & ObjectFeaturesEnum.LiveData) > 0);


const _hasWriteDown = (o: IEntityValueDeviceModel |
    IEntityValueTankModel |
    IEntityValueWellModel |
    ITankBattery |
    IEntityValueTagModel |
    ITagModel) => o.features !== undefined && (o.features & ObjectFeaturesEnum.Writedown) > 0;

const ObjectTagValueUtils = {

    createGridRows: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined,
        routeContext: RouteContextType,
        mode: ObjectTagValuesMode,
        offline: boolean,
        liveDataState: LiveDataStateItem | undefined,
        filteredCategories: ITagCategory[],
        summaryListingTATs: number[],
        isPhone: boolean,
        inWriteDownMode: boolean,
        tankLevelInches: boolean): Array<RowDef> {

        //clone the initial model so that any changes made to the data will not affect the original object
        const clonedModel = _.cloneDeep(model);


        const allTags = filteredCategories.find(o => o.id === ALL_TAGS_CATEGORY_ID) !== undefined || offline;
        const summaryTags = filteredCategories.find(o => o.id === SUMMARY_LISTING_TAGS_CATEGORY_ID) !== undefined && !offline;
        const tags = mode === ObjectTagValuesMode.ManualEntry ?
            clonedModel?.tags.filter(o => o.features && (o.features & ObjectFeaturesEnum.ManualEntry) > 0) :
            clonedModel?.tags ?
                allTags ?
                    clonedModel.tags :
                    summaryTags ?
                        summaryListingTATs.reduce((result: IEntityValueTagModel[] | ITagModel[], currentValue) => {
                            return result.concat(clonedModel.tags.filter(o => o.tatID === currentValue));
                        }, []) :

                        filteredCategories.reduce((result: IEntityValueTagModel[] | ITagModel[], currentValue) => {
                            return result.concat(clonedModel.tags.filter(o => o.categoryID === currentValue.id));
                        }, []) :
                [];

        let rows = tags?.map((o) => {

            const category = clonedModel?.categories?.find(v => (v.id === o.categoryID && o.categoryID) || v.text === o.categoryName);
            return projectRow(category?.text ?? i18n.t('tag.ungrouped'),
                undefined,
                category?.order ?? UNGROUPED_CATEGORY_ID,
                o,
                clonedModel?.objectType === eLynxObjectTypeEnum.Tank ? (clonedModel as IEntityValueTankModel).tankStyle : undefined,
                routeContext,
                offline,
                liveDataState,
                tankLevelInches);

        }) ?? new Array<RowDef>();

        switch (clonedModel?.objectType) {
            case eLynxObjectTypeEnum.Device: {
                const device = clonedModel as IEntityValueDeviceModel;
                if (device.tanks) {

                    device.tanks.forEach((o) => {

                        const tank = o as IEntityValueTankModel;

                        const tankTags = mode === ObjectTagValuesMode.ManualEntry ?
                            tank?.tags.filter(o => o.features && (o.features & ObjectFeaturesEnum.ManualEntry) > 0) :
                            tank?.tags && (allTags || filteredCategories.find(o => o.objectID === tank.id && o.objectType === tank.objectType)) ?
                                tank.tags : [];

                        const tankRouteContext = {
                            ...routeContext,
                            tankID: tank.id
                        };

                        rows = rows.concat(tankTags.map((v) => {
                            return projectRow(tank.displayText as string,
                                getTankRoute(UIViewEnum.Tank_Details, routeContext.groupID, device.id, undefined, tank.id),
                                tank.subTypeID === 1 ? OIL_TANK_CATEGORY_ID : WATER_TANK_CATEGORY_ID,
                                v,
                                tank.tankStyle,
                                tankRouteContext,
                                offline,
                                liveDataState,
                                tankLevelInches);
                        }));
                    });
                }

                break;
            }

            case eLynxObjectTypeEnum.Well: {
                const well = clonedModel as IEntityValueWellModel;
                if (well.devices) {

                    well.devices.forEach((o) => {

                        const device = o as IEntityValueDeviceModel;

                        const deviceTags = mode === ObjectTagValuesMode.ManualEntry ?
                            device?.tags.filter(o => o.features && (o.features & ObjectFeaturesEnum.ManualEntry) > 0) :
                            //device?.tags;
                            device?.tags && (allTags || filteredCategories.find(o => o.objectID === device.id && o.objectType === device.objectType)) ?
                                summaryTags ?
                                    summaryListingTATs.reduce((result: IEntityValueTagModel[] | ITagModel[], currentValue) => {
                                        return result.concat(device.tags.filter(o => o.tatID === currentValue));
                                    }, []) :

                                    device.tags : [];

                        const deviceRouteContext = {
                            ...routeContext,
                            deviceID: device.id
                        };

                        rows = rows.concat(deviceTags.map((v) => {
                            return projectRow(device.displayText as string,
                                getDeviceRoute(UIViewEnum.Device_Details, routeContext.groupID, well.id, device.id),
                                WELL_DEVICE_CATEGORY_ID,
                                v,
                                undefined,
                                deviceRouteContext,
                                offline,
                                liveDataState,
                                tankLevelInches);
                        }));
                    });
                }

                break;
            }

            case eLynxObjectTypeEnum.TankBattery: {
                const tankBattery = clonedModel as ITankBattery;
                if (offline && tankBattery.tanks) {

                    tankBattery.tanks.forEach((tank) => {

                        const tankTags = mode === ObjectTagValuesMode.ManualEntry ?
                            tank?.tags.filter(o => o.features && (o.features & ObjectFeaturesEnum.ManualEntry) > 0) :
                            tank?.tags;

                        const tankRouteContext = {
                            ...routeContext,
                            tankID: tank.id
                        };

                        rows = rows.concat(tankTags.map((v) => {
                            return projectRow(tank.displayText as string,
                                getTankRoute(UIViewEnum.Tank_Details, routeContext.groupID, undefined, tankBattery.id, tank.id),
                                tank.subTypeID === 1 ? OIL_TANK_CATEGORY_ID : WATER_TANK_CATEGORY_ID,
                                v,
                                tankBattery.tankStyles.find(v => v.id === tank.tankStyleID),
                                tankRouteContext,
                                offline,
                                liveDataState,
                                tankLevelInches);
                        }));
                    });
                }

                break;
            }
        }

        //if (isPhone && inWriteDownMode) {
        if (inWriteDownMode) {
            rows = rows.filter(o => o['hasWriteDown'] === true);
        }


        rows = Enumerable.from(rows)
            .orderBy(o => o.order)
            .thenBy(o => o.category)
            .toArray();

        return rows;
    },
    createGridColumns: function (theme: Theme,
        mode: ObjectTagValuesMode,
        isDesktop: boolean,
        offline: boolean,
        useLKVOffline: boolean,
        grouped: boolean,
        showTagDescription: boolean,
        configurationEnabled: boolean,
        configurationSelected: boolean,
        columnOptions: ObjectTagValuesColumnOptionsState,
        isElynxAdmin: boolean
    ): ColDef[] {

        if (isDesktop && !offline && mode === ObjectTagValuesMode.Values) {
            return createDesktopGridColumns(
                theme,
                offline,
                useLKVOffline,
                false,
                grouped,
                showTagDescription,
                configurationEnabled,
                configurationSelected,
                columnOptions,
                isElynxAdmin
            );
        } else {
            return createMobileGridColumns(
                theme,
                offline,
                useLKVOffline,
                mode === ObjectTagValuesMode.ManualEntry,
                grouped,
                configurationEnabled,
                configurationSelected,
                columnOptions,
                isElynxAdmin
            );
        }
    },

    // For mobile we have to an approximation of row heights, otherwise the ag grid will set the row
    // height to a fixed height for all rows.
    //getRowHeight: function(params: ICellRendererParams, theme: Theme): number {
    getRowHeight: function (params: RowHeightParams, theme: Theme): number {

        if (params.node.group) {
            return parseFloat(theme.spacing(6));
        } else {
            let approximateLinesOfText = Math.max(params.data.name.length / 16, 2);

            // add room for the timestamp line
            approximateLinesOfText += 2;

            return approximateLinesOfText > 3 ? approximateLinesOfText * parseFloat(theme.spacing(2.25)) : parseFloat(theme.spacing(8));
        }
    },
    hasLiveData: function (entity: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | ITagModel): boolean {
        return _hasLiveData(entity);
    },
    hasDemandScan: function (entity: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery): boolean {

        let returnData = false;

        if (_hasLiveData(entity)) {
            let tags = entity.tags.filter(o => _hasLiveData(o));

            switch (entity.objectType) {
                case eLynxObjectTypeEnum.Device: {
                    const device = entity as IEntityValueDeviceModel;
                    tags = tags.concat(device.tanks.flatMap(o => o.tags).filter(o => _hasLiveData(o)));
                    break;
                }
                case eLynxObjectTypeEnum.Well: {
                    const well = entity as IEntityValueWellModel;
                    tags = tags.concat(well.devices.flatMap(o => o.tags).filter(o => _hasLiveData(o)));
                    break;
                }

                default:
                    break;
            }

            returnData = tags.length > 0;
        }


        return returnData;
    },
    hasWriteDown: function (entity: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | IEntityValueTagModel | ITagModel): boolean {

        let returnData = false;

        const tagEntity = entity as ITagModel;
        //if (entity.objectType === eLynxObjectTypeEnum.Tag) {
        if (tagEntity) {
            return _hasWriteDown(entity);
        }

        if (_hasWriteDown(entity)) {
            const obj = entity as IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery;
            let tags = obj.tags.filter(o => _hasWriteDown(o));

            switch (entity.objectType) {
                case eLynxObjectTypeEnum.Device: {
                    const device = entity as IEntityValueDeviceModel;
                    tags = tags.concat(device.tanks.flatMap(o => o.tags).filter(o => _hasWriteDown(o)));
                    break;
                }

                case eLynxObjectTypeEnum.Well: {
                    const well = entity as IEntityValueWellModel;
                    tags = tags.concat(well.devices.flatMap(o => o.tags).filter(o => _hasWriteDown(o)));
                    break;
                }

                default:
                    break;
            }

            returnData = tags.length > 0;
        }

        return returnData;
    },
    getCommandStatus: function (value: string): CommandStatusEnum {
        switch (value) {
            case 'InProgress':
                return CommandStatusEnum.InProgress;
            case 'Received':
                return CommandStatusEnum.Received;
            case 'Sent':
                return CommandStatusEnum.Sent;
            case 'Success':
                return CommandStatusEnum.Success;
            case 'Failed':
                return CommandStatusEnum.Failed;

            default:
                return CommandStatusEnum.None;

        }
        // export enum CommandStatusEnum {
        //     None = -1,
        //     InProgress = 0,
        //     Received = 1,       // Specifically for WellLynxXI to indicate the unit received the command
        //     Sent = 2,           // Specifically for WellLynxXI (per Dan not currently used)
        //     Success = 3,
        //     Failed = 4
        // }

    },
    getNavigationObjects: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined,
        /*routeContext: RouteContextType*/groupID: number): Array<IKeyValuePair<string, string>> | undefined {

        //@@foo remove routeContext and just use groupid
        switch (model?.objectType) {
            case eLynxObjectTypeEnum.Device: {
                const device = model as IEntityValueDeviceModel;
                if (device.tanks) {

                    return device.tanks.map((o) => {
                        const tank = o as IEntityValueTankModel;
                        return {
                            Key: tank.displayText,
                            Value: getTankRoute(UIViewEnum.Tank_Details, groupID, device.id, undefined, tank.id)
                        } as IKeyValuePair<string, string>;
                    });
                }

                break;
            }

            case eLynxObjectTypeEnum.Well: {
                const well = model as IEntityValueWellModel;
                if (well.devices) {

                    return well.devices.map((o) => {
                        const device = o as IEntityValueDeviceModel;
                        return {
                            Key: o.displayText,
                            Value: getDeviceRoute(UIViewEnum.Device_Details, groupID, well.id, device.id)
                        } as IKeyValuePair<string, string>;
                    });
                }

                break;
            }

            case eLynxObjectTypeEnum.TankBattery: {
                const tankBattery = model as ITankBattery;
                if (tankBattery.tanks) {

                    return tankBattery.tanks.map((tank) => {
                        return {
                            Key: tank.displayText,
                            Value: getTankRoute(UIViewEnum.Tank_Details, groupID, undefined, tankBattery.id, tank.id)
                        } as IKeyValuePair<string, string>;
                    });
                }

                break;
            }
        }
    },
    shouldDemandScanBeforeWriteDown: function (model: IEntityValueDeviceModel | IEntityValueTankModel,
        lastDemandScanTimestamp: Date | undefined): boolean {
        const date = addMinutes(new Date(), -4);
        return ((model.demandScanBeforeWrite ?? false) && (!lastDemandScanTimestamp || lastDemandScanTimestamp?.getTime() < date.getTime()));
    },
    getDefaultTagCategory: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined,
        defaultSummaryListingTags: boolean): ITagCategory {

        return model?.objectType !== eLynxObjectTypeEnum.Device ||
            (model as IEntityValueDeviceModel).deviceType === DeviceTypesEnum.StandaloneTank ?
            {
                ...allTagsCategory,
                selected: true
            } :
            defaultSummaryListingTags ?
                {
                    ...summaryListingTagsCategory,
                    selected: defaultSummaryListingTags
                } : {
                    ...allTagsCategory,
                    selected: true
                };
    },
    getTagCategories: function (model: IEntityValueDeviceModel |
        IEntityValueTankModel | 
        IEntityValueWellModel | 
        ITankBattery | undefined, 
        defaultSummaryListingTags: boolean,
        writeDownOnly?: boolean): ITagCategory[] {

        let categories: ITagCategory[] = [{
            ...allTagsCategory,
            selected: model?.objectType !== eLynxObjectTypeEnum.Device ||
                (model?.objectType === eLynxObjectTypeEnum.Device &&
                    (model as IEntityValueDeviceModel).deviceType === DeviceTypesEnum.StandaloneTank) ?
                true :
                !defaultSummaryListingTags
        }];


        if (model?.objectType === eLynxObjectTypeEnum.Device && !writeDownOnly &&
            !((model as IEntityValueDeviceModel).deviceType === DeviceTypesEnum.StandaloneTank)) {
            categories.push({
                ...summaryListingTagsCategory,
                selected: defaultSummaryListingTags
            });
        }

        if (model?.categories) {
            categories = categories.concat(model?.categories.sort((o, v) => (o.order ?? 0) - (v.order ?? 0)) ?? []);
        }

        const tags = (writeDownOnly ?
                    model?.tags?.filter(o => o.features && (o.features & ObjectFeaturesEnum.Writedown) > 0) :
                    model?.tags) ?? [];

        if (tags.find(o => o.categoryID === UNGROUPED_CATEGORY_ID)) {
            categories.push({ ...ungroupedTagCategory });
        }

        switch (model?.objectType) {
            case eLynxObjectTypeEnum.Device: {
                const device = model as IEntityValueDeviceModel;
                if (device.tanks) {

                    const oilTanks = device.tanks.filter(o => o.subTypeID === DeviceSubTypeEnum.Oil);
                    const waterTanks = device.tanks.filter(o => o.subTypeID === DeviceSubTypeEnum.Water);

                    let tankTags = writeDownOnly ? 
                                    oilTanks.flatMap(o => o.tags).filter(o => o.features && (o.features & ObjectFeaturesEnum.Writedown) > 0) : 
                                    oilTanks.flatMap(o => o.tags);

                    if (tankTags.length > 0) {                                    

                        categories = categories.concat(device.tanks.filter(o => o.subTypeID === DeviceSubTypeEnum.Oil).map((o, index) => {
                            return {
                                id: OIL_TANK_CATEGORY_ID + index,
                                text: o.displayText,
                                order: OIL_TANK_CATEGORY_ID + index + 1,
                                objectID: o.id,
                                objectType: o.objectType

                            } as ITagCategory;
                        }));
                    }

                    tankTags = writeDownOnly ? 
                                    waterTanks.flatMap(o => o.tags).filter(o => o.features && (o.features & ObjectFeaturesEnum.Writedown) > 0) : 
                                    waterTanks.flatMap(o => o.tags);

                    if (tankTags.length > 0) {

                        categories = categories.concat(device.tanks.filter(o => o.subTypeID === DeviceSubTypeEnum.Water).map((o, index) => {
                            return {
                                id: WATER_TANK_CATEGORY_ID + index,
                                text: o.displayText,
                                order: WATER_TANK_CATEGORY_ID + index + 1,
                                objectID: o.id,
                                objectType: o.objectType

                            } as ITagCategory;
                        }));
                    }

                }

                break;
            }

            case eLynxObjectTypeEnum.Well: {
                const well = model as IEntityValueWellModel;


                categories = categories.concat(well.devices.map((o, index) => {
                    return {
                        id: WELL_DEVICE_CATEGORY_ID + index,
                        text: o.displayText,
                        order: WELL_DEVICE_CATEGORY_ID + index + 1,
                        objectID: o.id,
                        objectType: o.objectType

                    } as ITagCategory;
                }));

                break;
            }
            //defaut:
            //    break;
        }

        return categories;
    },
    getViewCategories: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined, categories: ITagCategory[], view: ObjectTagValuesViewEnum): ITagCategory[] {
        if (view === ObjectTagValuesViewEnum.Chart && model && categories) {
            const tags = this.getTrendableTags(model);

            const initialCategories = categories.filter(o => o.id === ALL_TAGS_CATEGORY_ID || o.id === SUMMARY_LISTING_TAGS_CATEGORY_ID);

            const usedCategories = tags.reduce((result: ITagCategory[], currentValue) => {
                return result.concat(categories.filter(o => o.id === currentValue.categoryID));
            }, initialCategories)
                .filter((n, i, arr) => arr.findIndex(o => o.id === n.id) === i) ?? [];

            return [
                ...usedCategories
            ];
        }

        return categories ?? [];
    },
    getTagAndTatIDs: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined): Array<ITagAndTatID> {
        const result = new Array<ITagAndTatID>();
        const tags: Array<ITagModel> = this.getTrendableTags(model);

        const distinctKeys: Set<string> = new Set<string>();

        tags.forEach(o => {
            const key = `${o.id}:${o.tatID}`;
            if (!distinctKeys.has(key)) {
                distinctKeys.add(key);
                result.push({
                    iD: o.id,
                    tatID: o.tatID as number
                } as ITagAndTatID);
            }
        });

        return result;
    },
    getTrendableTags: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined): Array<ITagModel> {
        let tags: Array<ITagModel> = model?.tags?.filter(o => o.trendingDefault === true) ?? [];
        switch (model?.objectType) {
            case eLynxObjectTypeEnum.Well: {
                const well = model as IEntityValueWellModel;
                well.devices?.forEach(device => {
                    tags = tags.concat(this.getTrendableTags(device));
                });
                break;
            }
            case eLynxObjectTypeEnum.Device: {
                const device = model as IEntityValueDeviceModel;
                device.tanks?.forEach(tank => {
                    tags = tags.concat(this.getTrendableTags(tank));
                });
                break;
            }
            // case eLynxObjectTypeEnum.TankBattery:
            // case eLynxObjectTypeEnum.Tank:
            //     break;
        }
        return tags;
    },
    createTagChartGroups: function (model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined,
        tagHistories: Array<ITagHistoryItem> | undefined,
        listTextTagMapping: IListTextTagMapping_V2 | undefined,
        filteredCategories: ITagCategory[],
        summaryListingTATs: number[],
        offline: boolean): Array<TagValueChartGroup> {

        const allTags = filteredCategories.find(o => o.id === ALL_TAGS_CATEGORY_ID) !== undefined || offline;
        const summaryTags = filteredCategories.find(o => o.id === SUMMARY_LISTING_TAGS_CATEGORY_ID) !== undefined && !offline;
        const trendableTags = this.getTrendableTags(model);

        const tags = trendableTags ?
            allTags ?
                trendableTags :
                summaryTags ?
                    summaryListingTATs.reduce((result: IEntityValueTagModel[] | ITagModel[], currentValue) => {
                        return result.concat(trendableTags.filter(o => o.tatID === currentValue));
                    }, []) :

                    filteredCategories.reduce((result: IEntityValueTagModel[] | ITagModel[], currentValue) => {
                        return result.concat(trendableTags.filter(o => o.categoryID === currentValue.id));
                    }, []) :
            [];

        const groups = new Array<TagValueChartGroup>();
        const maxOrder = model && model.categories && model.categories.length > 0 ?
            Enumerable.from(model?.categories).max(c => c.order ?? 0) + 1 :
            0;

        tagHistories?.forEach(historyItem => {

            // temporary change for hotfix to set the color to what would be 
            // the default trend color for the tat.  There is a backend fix
            // once the hotfix goes out this will be removed.
            //historyItem.trendColor = '#1E90FF';

            //const tag = tags.find(t => t.id === history.tagID as number);
            const matchingTags = tags.filter(t => t.id === historyItem.tagID as number);
            matchingTags.forEach(tag => {
                const isTankTag = tag.categoryID === 999999 && tag.tagObject?.objectType === 17;
                const category = isTankTag ?
                    {
                        text: tag.tagObject?.objectName,
                        order: tag.categoryID
                    } :
                    model?.categories.find(c => c.id === tag.categoryID as number);

                let group = groups.find(o => o.name === category?.text);

                if (!group && !isTankTag && tag.categoryID as number === UNGROUPED_CATEGORY_ID)
                    group = groups.find(o => o.name === ungroupedTagCategory.text);
                if (!group) {
                    group = {
                        name: category?.text ?? ungroupedTagCategory.text,
                        order: category?.text ? category?.order : maxOrder,
                        items: []
                    } as TagValueChartGroup;
                    groups.push(group);
                }
                group.items.push({
                    tagDetailInfo: {
                        contextObject: {
                            ...tag,
                            objectName: tag.objectName ?? tag.displayText
                        },
                        tagUnits: tag?.unitDisplayName,

                        historyRowCount: 0,
                        refreshSeconds: 0,
                        tagObjectType: tag.tagObject?.objectType ?? eLynxObjectTypeEnum.Device,
                        tagObjectName: tag.tagObject?.objectName ?? tag.displayText,
                        tagType: TagTypeEnum.Analog,
                        //hybridServicesR1Enabled: false,
                        dataType: tag.dataType,
                        defaultMaxRowCount: 0,
                        uomID: tag.uomID,
                        features: tag.features,



                    } as ITagViewState,
                    historyItem: historyItem,
                    listTextTagMapping: listTextTagMapping
                } as TagValueChartListItem);
            });
        });

        // Sort TAT groups to the top, then Tag groups in order to match the grid display order.
        // If the orders are then same, then sort by name.
        groups.sort((l, r) => {
            const lorder = (l.order ?? 0);
            const rorder = (r.order ?? 0);

            return lorder === rorder ?
                ('' + l.name).localeCompare(r.name) :
                lorder - rorder;
        });

        groups.forEach(g => {
            g.items.sort((l, r) => ('' + l.tagDetailInfo?.contextObject?.displayText).localeCompare('' + r.tagDetailInfo?.contextObject?.displayText));
        });

        return groups;
    },
};

export function getViewType(objectType: eLynxObjectTypeEnum | undefined): ObjectTagValuesTypeEnum | undefined {
    return objectType === eLynxObjectTypeEnum.Device
        ? ObjectTagValuesTypeEnum.Device
        : objectType === eLynxObjectTypeEnum.Well
            ? ObjectTagValuesTypeEnum.Well
            : objectType === eLynxObjectTypeEnum.Tank
                ? ObjectTagValuesTypeEnum.Tank
                : objectType === eLynxObjectTypeEnum.TankBattery
                    ? ObjectTagValuesTypeEnum.TankBattery
                    : undefined;
}

export default ObjectTagValueUtils;

export { UNGROUPED_CATEGORY_ID, OIL_TANK_CATEGORY_ID, WATER_TANK_CATEGORY_ID, WELL_DEVICE_CATEGORY_ID };
